import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import SubscriptionTable from './components/SubscriptionTable';
import NotificationPanel from './components/NotificationPanel';
import BulkRenewalModal from './components/BulkRenewalModal';
import FilterPanel from './components/FilterPanel';

const SubscriptionManagement = () => {
  const [activeTab, setActiveTab] = useState('ending-soon');
  const [selectedClients, setSelectedClients] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    subscriptionType: 'all',
    dateRange: 'all',
    renewalHistory: 'all'
  });

  // Mock data for subscriptions ending soon
  const endingSoonSubscriptions = [
    {
      id: 1,
      clientName: "Ahmed Al-Rashid",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      subscriptionType: "Premium Monthly",
      endDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
      daysRemaining: 5,
      status: "ending-soon",
      phone: "+971-50-123-4567",
      lastRenewal: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    },
    {
      id: 2,
      clientName: "Sarah Johnson",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      subscriptionType: "Basic Monthly",
      endDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
      daysRemaining: 3,
      status: "ending-soon",
      phone: "+971-50-234-5678",
      lastRenewal: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    },
    {
      id: 3,
      clientName: "Mohammed Hassan",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      subscriptionType: "Premium Yearly",
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      daysRemaining: 7,
      status: "ending-soon",
      phone: "+971-50-345-6789",
      lastRenewal: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)
    },
    {
      id: 4,
      clientName: "Fatima Al-Zahra",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      subscriptionType: "Basic Monthly",
      endDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
      daysRemaining: 2,
      status: "ending-soon",
      phone: "+971-50-456-7890",
      lastRenewal: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    }
  ];

  // Mock data for expired subscriptions
  const expiredSubscriptions = [
    {
      id: 5,
      clientName: "Omar Abdullah",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
      subscriptionType: "Premium Monthly",
      endDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      daysOverdue: 3,
      status: "expired",
      phone: "+971-50-567-8901",
      lastRenewal: new Date(Date.now() - 33 * 24 * 60 * 60 * 1000)
    },
    {
      id: 6,
      clientName: "Layla Mahmoud",
      avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
      subscriptionType: "Basic Monthly",
      endDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
      daysOverdue: 10,
      status: "expired",
      phone: "+971-50-678-9012",
      lastRenewal: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000)
    },
    {
      id: 7,
      clientName: "Khalid Al-Mansouri",
      avatar: "https://images.unsplash.com/photo-1507591064344-4c6ce005b128?w=150&h=150&fit=crop&crop=face",
      subscriptionType: "Premium Yearly",
      endDate: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
      daysOverdue: 15,
      status: "expired",
      phone: "+971-50-789-0123",
      lastRenewal: new Date(Date.now() - 380 * 24 * 60 * 60 * 1000)
    }
  ];

  // Mock notifications data
  const notifications = [
    {
      id: 1,
      clientName: "Ahmed Al-Rashid",
      type: "ending-soon",
      message: "Subscription ending in 5 days",
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      status: "pending"
    },
    {
      id: 2,
      clientName: "Sarah Johnson",
      type: "ending-soon",
      message: "Subscription ending in 3 days",
      timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000),
      status: "sent"
    },
    {
      id: 3,
      clientName: "Omar Abdullah",
      type: "expired",
      message: "Subscription expired 3 days ago",
      timestamp: new Date(Date.now() - 30 * 60 * 1000),
      status: "pending"
    }
  ];

  const getCurrentSubscriptions = () => {
    return activeTab === 'ending-soon' ? endingSoonSubscriptions : expiredSubscriptions;
  };

  const filteredSubscriptions = getCurrentSubscriptions().filter(subscription => {
    const matchesSearch = subscription.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         subscription.phone.includes(searchTerm);
    
    const matchesType = filters.subscriptionType === 'all' || 
                       subscription.subscriptionType.toLowerCase().includes(filters.subscriptionType.toLowerCase());
    
    return matchesSearch && matchesType;
  });

  const handleSelectClient = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAll = () => {
    if (selectedClients.length === filteredSubscriptions.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredSubscriptions.map(sub => sub.id));
    }
  };

  const handleBulkRenewal = () => {
    if (selectedClients.length > 0) {
      setShowBulkModal(true);
    }
  };

  const handleExport = () => {
    // Mock export functionality
    const data = filteredSubscriptions.map(sub => ({
      'Client Name': sub.clientName,
      'Subscription Type': sub.subscriptionType,
      'End Date': sub.endDate.toLocaleDateString(),
      'Status': sub.status,
      'Days Remaining/Overdue': activeTab === 'ending-soon' ? sub.daysRemaining : sub.daysOverdue
    }));
    
    console.log('Exporting data:', data);
    // In real implementation, this would generate and download a CSV/Excel file
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/dashboard-overview" className="flex items-center">
                <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center mr-3">
                  <Icon name="Dumbbell" size={20} color="white" />
                </div>
                <span className="text-xl font-bold text-gray-900">FitAdmin Pro</span>
              </Link>
            </div>
            
            <div className="flex items-center space-x-4">
              <button className="p-2 text-gray-400 hover:text-gray-500">
                <Icon name="Bell" size={20} />
              </button>
              <div className="flex items-center space-x-2">
                <Image
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face"
                  alt="Admin Avatar"
                  className="w-8 h-8 rounded-full"
                />
                <span className="text-sm font-medium text-gray-700">Admin</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <nav className="w-64 bg-white shadow-sm min-h-screen">
          <div className="p-4">
            <ul className="space-y-2">
              <li>
                <Link
                  to="/dashboard-overview"
                  className="flex items-center px-4 py-2 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  <Icon name="LayoutDashboard" size={20} className="mr-3" />
                  Dashboard Overview
                </Link>
              </li>
              <li>
                <Link
                  to="/client-management"
                  className="flex items-center px-4 py-2 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  <Icon name="Users" size={20} className="mr-3" />
                  Client Management
                </Link>
              </li>
              <li>
                <Link
                  to="/subscription-management"
                  className="flex items-center px-4 py-2 text-white bg-green-600 rounded-lg"
                >
                  <Icon name="Calendar" size={20} className="mr-3" />
                  Subscriptions
                </Link>
              </li>
              <li>
                <Link
                  to="/admin-account-management"
                  className="flex items-center px-4 py-2 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  <Icon name="Settings" size={20} className="mr-3" />
                  Admin Accounts
                </Link>
              </li>
            </ul>
          </div>
        </nav>

        {/* Main Content */}
        <div className="flex-1 flex">
          <main className="flex-1 p-6">
            <div className="max-w-6xl mx-auto">
              {/* Page Header */}
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Subscription Management</h1>
                <p className="text-gray-600">Monitor and manage client subscription lifecycles</p>
              </div>

              {/* Action Bar */}
              <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search clients..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <Icon name="Filter" size={20} className="mr-2" />
                      Filters
                    </button>
                  </div>

                  <div className="flex items-center space-x-3">
                    {selectedClients.length > 0 && (
                      <button
                        onClick={handleBulkRenewal}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        <Icon name="RefreshCw" size={20} className="mr-2" />
                        Bulk Renewal ({selectedClients.length})
                      </button>
                    )}
                    <button
                      onClick={handleExport}
                      className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <Icon name="Download" size={20} className="mr-2" />
                      Export
                    </button>
                  </div>
                </div>

                {/* Filter Panel */}
                {showFilters && (
                  <FilterPanel 
                    filters={filters}
                    setFilters={setFilters}
                    onClose={() => setShowFilters(false)}
                  />
                )}
              </div>

              {/* Tabs */}
              <div className="bg-white rounded-lg shadow-sm mb-6">
                <div className="border-b border-gray-200">
                  <nav className="flex space-x-8 px-6">
                    <button
                      onClick={() => setActiveTab('ending-soon')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'ending-soon' ?'border-green-500 text-green-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center">
                        <Icon name="AlertTriangle" size={16} className="mr-2" />
                        Ending Soon ({endingSoonSubscriptions.length})
                      </div>
                    </button>
                    <button
                      onClick={() => setActiveTab('expired')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'expired' ?'border-green-500 text-green-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center">
                        <Icon name="XCircle" size={16} className="mr-2" />
                        Expired ({expiredSubscriptions.length})
                      </div>
                    </button>
                  </nav>
                </div>

                {/* Subscription Table */}
                <SubscriptionTable
                  subscriptions={filteredSubscriptions}
                  selectedClients={selectedClients}
                  onSelectClient={handleSelectClient}
                  onSelectAll={handleSelectAll}
                  activeTab={activeTab}
                />
              </div>
            </div>
          </main>

          {/* Notification Panel */}
          <NotificationPanel notifications={notifications} />
        </div>
      </div>

      {/* Bulk Renewal Modal */}
      {showBulkModal && (
        <BulkRenewalModal
          selectedClients={selectedClients}
          subscriptions={getCurrentSubscriptions()}
          onClose={() => setShowBulkModal(false)}
          onRenewal={() => {
            setShowBulkModal(false);
            setSelectedClients([]);
          }}
        />
      )}
    </div>
  );
};

export default SubscriptionManagement;